import { useEffect, useState } from 'react';
import {
    Card, CardContent
} from '@/components/ui/card';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Pencil, Search, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getReviews, updateReview } from '@/api/reviews.api';
import RatingStars from './RatingStars';
import EditReviewDialog from './EditReviewDialog';

const PAGE_SIZE = 20;

const AllReviewsTab = () => {
    const [reviews, setReviews] = useState([]);
    const [summary, setSummary] = useState({ avgRating: 0, totalReviews: 0 });
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
    const [loading, setLoading] = useState(true);

    const [ratingFilter, setRatingFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
    const [reloadKey, setReloadKey] = useState(0);
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const res = await getReviews({
                    page,
                    limit: PAGE_SIZE,
                    rating: ratingFilter === 'all' ? undefined : ratingFilter,
                    status: statusFilter,
                    search: appliedSearch || undefined
                });
                if (!mounted) return;
                setReviews(res.data?.data || []);
                setSummary(res.data?.summary || { avgRating: 0, totalReviews: 0 });
                setPagination(res.data?.pagination || { page: 1, totalPages: 1 });
            } catch (err) {
                if (mounted) toast.error('Failed to load reviews');
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [page, ratingFilter, statusFilter, appliedSearch, reloadKey]);

    // ---- Edit / status (Super Admin) ----
    const [editing, setEditing] = useState(null);
    const [statusSavingId, setStatusSavingId] = useState(null);

    const replaceReview = (updated) => {
        if (!updated?.id) return;
        setReviews((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    };

    const toggleStatus = async (r) => {
        const next = r.status === 'INACTIVE' ? 'ACTIVE' : 'INACTIVE';
        setStatusSavingId(r.id);
        replaceReview({ ...r, status: next }); // optimistic
        try {
            const res = await updateReview(r.id, { status: next });
            replaceReview(res.data?.data);
            toast.success(next === 'ACTIVE' ? 'Review is now active' : 'Review hidden (inactive)');
        } catch (err) {
            replaceReview(r); // roll back
            toast.error(err.response?.data?.detail || 'Failed to update status');
        } finally {
            setStatusSavingId(null);
        }
    };


    const runSearch = () => {
        setPage(1);
        setAppliedSearch(search.trim());
    };

    const clearSearch = () => {
        setSearch('');
        setAppliedSearch('');
        setPage(1);
    };

    return (
        <div className="space-y-4">
            {/* SUMMARY */}
            <div className="flex flex-wrap gap-3">
                <div className="rounded-lg border bg-white px-4 py-3 flex items-center gap-2">
                    <Star size={18} className="fill-amber-400 text-amber-400" />
                    <div>
                        <div className="text-lg font-bold leading-none">{summary.avgRating || 0}</div>
                        <div className="text-xs text-gray-500">Avg rating</div>
                    </div>
                </div>
                <div className="rounded-lg border bg-white px-4 py-3">
                    <div className="text-lg font-bold leading-none">{summary.totalReviews || 0}</div>
                    <div className="text-xs text-gray-500">Total reviews</div>
                </div>
            </div>

            {/* FILTERS */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        placeholder="Search review comments..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                        className="pl-9 pr-9"
                        data-testid="reviews-search-input"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                <Button variant="outline" onClick={runSearch} data-testid="reviews-search-button">Search</Button>

                <Select
                    value={ratingFilter}
                    onValueChange={(v) => { setRatingFilter(v); setPage(1); }}
                >
                    <SelectTrigger className="w-40" data-testid="reviews-rating-filter">
                        <SelectValue placeholder="Rating" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All ratings</SelectItem>
                        {[5, 4, 3, 2, 1].map((r) => (
                            <SelectItem key={r} value={String(r)}>{r} star{r > 1 ? 's' : ''}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select
                    value={statusFilter}
                    onValueChange={(v) => { setStatusFilter(v); setPage(1); }}
                >
                    <SelectTrigger className="w-40" data-testid="reviews-status-filter">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-48">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <Card className="border-none shadow-none bg-transparent">
                    <CardContent className="px-0">
                        <div className="rounded-lg border bg-white overflow-hidden">
                            <Table data-testid="reviews-table">
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead>Order #</TableHead>
                                        <TableHead>Restaurant</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Rating</TableHead>
                                        <TableHead>Comment</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {reviews.length > 0 ? reviews.map((r) => (
                                        <TableRow
                                            key={r.id}
                                            data-testid={`review-row-${r.id}`}
                                            className={r.status === 'INACTIVE' ? 'bg-gray-50/80 opacity-70' : ''}
                                        >
                                            <TableCell className="font-mono text-sm">
                                                {r.order?.orderNumber ? `#${r.order.orderNumber}` : '-'}
                                            </TableCell>
                                            <TableCell className="text-sm">{r.resturant?.name || '-'}</TableCell>
                                            <TableCell className="text-sm">{r.user?.name || 'Customer'}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <RatingStars rating={r.rating} />
                                                    <Badge variant="outline">{r.rating}/5</Badge>
                                                    {r.editedByAdminAt && (
                                                        <span className="text-[10px] font-medium text-orange-700" title={`Edited by admin${r.originalRating != null ? ` (customer gave ${r.originalRating}/5)` : ''}`}>
                                                            Edited
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm text-gray-600 max-w-[260px] truncate" title={r.comment}>
                                                {r.comment || <span className="text-gray-400 italic">No comment</span>}
                                            </TableCell>
                                            <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                                                {r.createdAt ? format(new Date(r.createdAt), 'dd MMM yyyy, hh:mm a') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={r.status !== 'INACTIVE'}
                                                        onCheckedChange={() => toggleStatus(r)}
                                                        disabled={statusSavingId === r.id}
                                                        aria-label={r.status !== 'INACTIVE' ? 'Deactivate review' : 'Activate review'}
                                                        data-testid={`review-status-toggle-${r.id}`}
                                                    />
                                                    <Badge
                                                        className={r.status !== 'INACTIVE'
                                                            ? 'bg-green-100 text-green-800 border-0'
                                                            : 'bg-gray-200 text-gray-700 border-0'}
                                                    >
                                                        {r.status !== 'INACTIVE' ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditing(r)}
                                                    data-testid={`review-edit-${r.id}`}
                                                >
                                                    <Pencil size={14} className="mr-1" />Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={8} className="text-center py-10 text-gray-500">
                                                No reviews found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>

                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between gap-3 mt-4 px-1">
                                <p className="text-sm text-gray-600">
                                    Page <span className="font-semibold text-gray-900">{pagination.page}</span> of{' '}
                                    <span className="font-semibold text-gray-900">{pagination.totalPages}</span>
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={page >= pagination.totalPages}
                                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            <EditReviewDialog
                review={editing}
                open={!!editing}
                onOpenChange={(v) => !v && setEditing(null)}
                onSaved={(updated) => {
                    replaceReview(updated);
                    setReloadKey((k) => k + 1); // refresh average / filters
                }}
            />
        </div>
    );
};

export default AllReviewsTab;
